import ExecutiveDiagramFrame from './ExecutiveDiagramFrame';

const colors = (theme, accent) => {
  const isDark = theme === 'dark';
  const accents = {
    amber: isDark ? '#fbbf24' : '#d97706',
    cyan: isDark ? '#22d3ee' : '#0891b2',
    indigo: isDark ? '#818cf8' : '#4f46e5',
    emerald: isDark ? '#34d399' : '#059669',
    slate: isDark ? '#64748b' : '#94a3b8',
  };
  return {
    bg: isDark ? '#0b1220' : '#f8fafc',
    grid: isDark ? '#1e293b' : '#e2e8f0',
    text: isDark ? '#cbd5e1' : '#475569',
    label: isDark ? '#f1f5f9' : '#0f172a',
    muted: isDark ? '#64748b' : '#94a3b8',
    accent: accents[accent] || accents.slate,
  };
};

const SvgWrap = ({ theme, accent, caption, children, viewBox = '0 0 560 320', svgClassName = '' }) => {
  const c = colors(theme, accent);
  return (
    <ExecutiveDiagramFrame theme={theme} caption={caption}>
      <svg viewBox={viewBox} className={`w-full h-auto ${svgClassName}`.trim()} role="img" aria-hidden>
        <rect width="100%" height="100%" fill={c.bg} />
        {children(c)}
      </svg>
    </ExecutiveDiagramFrame>
  );
};

export const AdvisoryLanesDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="slate" caption="Three advisory lanes · one governance standard" svgClassName="adv-lanes-svg">
    {(c) => (
      <>
        <defs>
          <filter id="adv-lanes-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <text x="280" y="36" textAnchor="middle" fill={c.label} fontSize="13" fontWeight="600" letterSpacing="3">
          ADVISORY PORTFOLIO
        </text>
        {[
          { x: 95, label: 'AI', sub: 'Portfolio & spend', color: colors(theme, 'amber').accent },
          { x: 280, label: 'DATA', sub: 'Estate & governance', color: colors(theme, 'cyan').accent },
          { x: 465, label: 'AGENTIC', sub: 'Authority & control', color: colors(theme, 'indigo').accent },
        ].map(({ x, label, sub, color }, i) => (
          <g key={label} className={`adv-lane adv-lane-${i}`}>
            <rect
              x={x - 72}
              y="72"
              width="144"
              height="168"
              rx="8"
              fill="none"
              stroke={color}
              strokeWidth="1.5"
              opacity="0.85"
              className="adv-lane-box"
            />
            <line x1={x} y1="108" x2={x} y2="204" stroke={color} strokeWidth="1" opacity="0.35" className="adv-lane-flow" />
            <circle r="3.5" fill={color} filter="url(#adv-lanes-glow)" className="adv-lane-flow-dot">
              <animateMotion dur="4s" repeatCount="indefinite" begin={`${i * 1.3}s`} path={`M ${x} 108 L ${x} 248`} />
            </circle>
            <text x={x} y="98" textAnchor="middle" fill={color} fontSize="11" fontWeight="700" letterSpacing="2">{label}</text>
            <text x={x} y="228" textAnchor="middle" fill={c.muted} fontSize="10" className="adv-lane-sub">{sub}</text>
          </g>
        ))}
        <line x1="167" y1="256" x2="393" y2="256" stroke={c.grid} strokeWidth="1" className="adv-lanes-converge" />
        <rect x="180" y="268" width="200" height="32" rx="4" fill="none" stroke={c.label} strokeWidth="1" opacity="0.35" className="adv-governance-box" />
        <text x="280" y="288" textAnchor="middle" fill={c.label} fontSize="10" letterSpacing="1.5">EXECUTIVE GOVERNANCE</text>
      </>
    )}
  </SvgWrap>
);

export const EngagementEntryDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="slate" caption="How an initial conversation proceeds" viewBox="0 0 560 240">
    {(c) => (
      <>
        {[
          { x: 90, label: 'Mandate', sub: 'Situation & constraints' },
          { x: 280, label: 'Conversation', sub: 'Independent perspective' },
          { x: 470, label: 'Direction', sub: 'What counsel fits' },
        ].map(({ x, label, sub }, i, arr) => (
          <g key={label}>
            <circle cx={x} cy="100" r="36" fill="none" stroke={c.accent} strokeWidth="1.5" />
            <text x={x} y="96" textAnchor="middle" fill={c.label} fontSize="11" fontWeight="600">{label}</text>
            <text x={x} y="112" textAnchor="middle" fill={c.muted} fontSize="9">{sub}</text>
            {i < arr.length - 1 && (
              <line x1={x + 40} y1="100" x2={arr[i + 1].x - 40} y2="100" stroke={c.grid} strokeWidth="1.5" markerEnd="url(#arrow)" />
            )}
          </g>
        ))}
        <defs>
          <marker id="arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 Z" fill={c.grid} />
          </marker>
        </defs>
        <text x="280" y="200" textAnchor="middle" fill={c.muted} fontSize="10">No pitch deck · No fixed package</text>
      </>
    )}
  </SvgWrap>
);

export const PortfolioFragmentationDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="amber" caption="Fragmented initiatives · consolidated executive view">
    {(c) => (
      <>
        <text x="140" y="40" textAnchor="middle" fill={c.muted} fontSize="10" letterSpacing="2">TODAY</text>
        <text x="420" y="40" textAnchor="middle" fill={c.accent} fontSize="10" letterSpacing="2">COUNSEL VIEW</text>
        {[[60, 80], [120, 120], [80, 180], [160, 200]].map(([x, y], i) => (
          <rect key={i} x={x} y={y} width="56" height="32" rx="4" fill="none" stroke={c.grid} strokeWidth="1" />
        ))}
        <line x1="220" y1="160" x2="280" y2="160" stroke={c.accent} strokeWidth="1.5" />
        <polygon points="280,155 292,160 280,165" fill={c.accent} />
        <rect x="300" y="100" width="200" height="120" rx="8" fill="none" stroke={c.accent} strokeWidth="1.5" />
        <line x1="320" y1="130" x2="480" y2="130" stroke={c.grid} strokeWidth="1" />
        <line x1="320" y1="160" x2="480" y2="160" stroke={c.grid} strokeWidth="1" />
        <line x1="320" y1="190" x2="480" y2="190" stroke={c.grid} strokeWidth="1" />
        <text x="400" y="122" textAnchor="middle" fill={c.label} fontSize="10">Portfolio</text>
        <text x="400" y="152" textAnchor="middle" fill={c.label} fontSize="10">Ownership</text>
        <text x="400" y="182" textAnchor="middle" fill={c.label} fontSize="10">Evidence</text>
      </>
    )}
  </SvgWrap>
);

export const DataEstateLayersDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="cyan" caption="Data estate · governance · AI readiness">
    {(c) => (
      <>
        {[
          { y: 220, w: 360, label: 'Operational systems', opacity: 0.4 },
          { y: 168, w: 300, label: 'Reference & quality', opacity: 0.6 },
          { y: 116, w: 240, label: 'Governance & access', opacity: 0.8 },
          { y: 64, w: 180, label: 'AI & analytics', opacity: 1 },
        ].map(({ y, w, label, opacity }) => (
          <g key={label} opacity={opacity}>
            <rect x={(560 - w) / 2} y={y} width={w} height="40" rx="4" fill="none" stroke={c.accent} strokeWidth="1.5" />
            <text x="280" y={y + 25} textAnchor="middle" fill={c.label} fontSize="11">{label}</text>
          </g>
        ))}
        <line x1="280" y1="104" x2="280" y2="260" stroke={c.grid} strokeWidth="1" strokeDasharray="4 4" />
      </>
    )}
  </SvgWrap>
);

export const AuthorityTriadDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="indigo" caption="Authority · accountability · evidence" viewBox="0 0 560 360">
    {(c) => (
      <>
        <polygon points="280,70 420,280 140,280" fill="none" stroke={c.accent} strokeWidth="1.5" opacity="0.7" />
        {[
          { x: 280, y: 78, label: 'Authority', sub: 'What may act' },
          { x: 130, y: 292, label: 'Accountability', sub: 'Who owns outcomes' },
          { x: 430, y: 292, label: 'Evidence', sub: 'What leadership inspects' },
        ].map(({ x, y, label, sub }) => (
          <g key={label}>
            <circle cx={x} cy={y} r="6" fill={c.accent} opacity="0.9" />
            <text x={x} y={y - 16} textAnchor="middle" fill={c.label} fontSize="12" fontWeight="600">{label}</text>
            <text x={x} y={y + 28} textAnchor="middle" fill={c.muted} fontSize="10">{sub}</text>
          </g>
        ))}
        <circle cx="280" cy="210" r="28" fill="none" stroke={c.grid} strokeWidth="1" />
        <text x="280" y="214" textAnchor="middle" fill={c.muted} fontSize="9">Agent</text>
      </>
    )}
  </SvgWrap>
);

export const OperatingDisciplinesDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="slate" caption="Strategy · architecture · review cadence">
    {(c) => (
      <>
        <line x1="120" y1="240" x2="440" y2="240" stroke={c.grid} strokeWidth="1" />
        {[
          { x: 120, label: 'Strategy', color: colors(theme, 'amber').accent },
          { x: 280, label: 'Architecture', color: colors(theme, 'cyan').accent },
          { x: 440, label: 'Cadence', color: colors(theme, 'indigo').accent },
        ].map(({ x, label, color }) => (
          <g key={label}>
            <line x1={x} y1="240" x2={x} y2="120" stroke={color} strokeWidth="1.5" />
            <rect x={x - 50} y="88" width="100" height="32" rx="4" fill="none" stroke={color} strokeWidth="1.5" />
            <text x={x} y="108" textAnchor="middle" fill={c.label} fontSize="11" fontWeight="600">{label}</text>
          </g>
        ))}
        <ellipse cx="280" cy="260" rx="120" ry="24" fill="none" stroke={c.label} strokeWidth="1" opacity="0.3" />
        <text x="280" y="265" textAnchor="middle" fill={c.label} fontSize="10" letterSpacing="2">MEASURABLE VALUE</text>
      </>
    )}
  </SvgWrap>
);

export const ConvergenceDiagram = ({ theme }) => (
  <SvgWrap theme={theme} accent="slate" caption="Separate workstreams · one executive table">
    {(c) => (
      <>
        {[
          { x: 80, label: 'Strategy' },
          { x: 200, label: 'Data' },
          { x: 360, label: 'Engineering' },
          { x: 480, label: 'Product' },
        ].map(({ x, label }) => (
          <g key={label}>
            <line x1={x} y1="60" x2={x} y2="140" stroke={c.grid} strokeWidth="1" />
            <rect x={x - 44} y="40" width="88" height="28" rx="4" fill="none" stroke={c.grid} strokeWidth="1" />
            <text x={x} y="58" textAnchor="middle" fill={c.muted} fontSize="10">{label}</text>
            <line x1={x} y1="140" x2="280" y2="220" stroke={c.accent} strokeWidth="1" opacity="0.5" />
          </g>
        ))}
        <rect x="200" y="220" width="160" height="48" rx="6" fill="none" stroke={c.label} strokeWidth="1.5" />
        <text x="280" y="250" textAnchor="middle" fill={c.label} fontSize="11" fontWeight="600">Executive table</text>
      </>
    )}
  </SvgWrap>
);

export const SERVICE_ILLUSTRATIONS = {
  'ai-advisory': '/images/blog/generative-ai-discipline.svg',
  'data-advisory': '/images/blog/mdm-reference-layer.svg',
  'agentic-ai-advisory': '/images/blog/agentic-ai-command-map.svg',
};
